import * as ReactDOMClient from 'react-dom/client';
import { useCallback, useMemo, useState, type FC } from 'react';
import Button from '2-core-elements/Buttons/Button';
import Icon from '2-core-elements/Icon/Icon';
import useTransactionHistoryQuery, { type TransactionDataType } from './Utils/useTransactionHistoryQuery';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { compareDesc, format, formatDate, isAfter, isSameDay, sub } from 'date-fns';
import { generateCsv } from '6-my-mcc/Pages/TransactionHistory/Utils/csvExporter';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import SelectCustom, { type SelectCustomOption } from '5-react-form-apps/FormElements/SelectCustom';

interface TransactionHistoryTableResultProps {
    transactions: TransactionDataType[];
}

const TransactionHistoryTableResult: FC<TransactionHistoryTableResultProps> = ({ transactions }) => {
    const transactionListSorted = useMemo((): { date: string; items: TransactionDataType[] }[] => {
        if (!transactions || transactions.length === 0) {
            return [];
        }
        const groupedTransactions: { [key: string]: TransactionDataType[] } = {};
        transactions.forEach(transaction => {
            const dateKey = format(new Date(transaction.transactionDate), 'yyyy-MM-dd');
            if (!groupedTransactions[dateKey]) {
                groupedTransactions[dateKey] = [];
            }
            groupedTransactions[dateKey].push(transaction);
        });
        return Object.entries(groupedTransactions).map(([date, items]) => {
            return { date, items };
        });
    }, [transactions]);

    if (transactions && transactions.length > 0) {
        return (
            <div className="transactions-table-wrapper">
                {transactionListSorted.map(({ date, items }) => {
                    return (
                        <div key={`transaction-table-group-${date}`} className="transactions-table-group">
                            <div className="transactions-table-header">{format(new Date(date), 'EEEE MMMM d, yyyy')}</div>
                            {items.map(transaction => {
                                return (
                                    <div key={`transaction-table-item-${transaction.id}`} className={`transactions-table-item ${transaction.description.toLowerCase().includes('rebate') ? 'rebate' : ''}`}>
                                        <div className="transactions-table-item-name">{transaction.description}</div>
                                        <div className="transactions-table-item-cost">
                                            {transaction.amount < 0 ? '-' : ''}${Math.abs(transaction.amount).toFixed(2)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        );
    }
    return (
        <div className="empty-table-wrapper">
            <div className="empty-table">
                <Icon className="svg-icon" icon="text-snippet" />
                <div className="empty-table-text">
                    <p className="empty-table-description">There are no transactions to display within the selected time period.</p>
                </div>
            </div>
        </div>
    );
};

const transactionOptions: SelectCustomOption[] = [
    { label: 'Past 30 Days', value: 'past-30-days' },
    { label: 'Past 12 Months', value: 'past-12-months' }
];

const TransactionHistory: FC = () => {
    const [transactionType, setTransactionType] = useState<SelectCustomOption>(transactionOptions[0]);

    // Calculate startDay and endDay based on selection
    const today = new Date();
    const endDay = today.toISOString().slice(0, 10);
    const startDate = new Date(today);
    if (transactionType.value === 'past-30-days') {
        startDate.setDate(startDate.getDate() - 30);
    } else {
        startDate.setFullYear(startDate.getFullYear() - 1);
    }
    const startDay = startDate.toISOString().slice(0, 10);

    const { transactionHistoryQuery, isTransactionHistoryQueryRetrieve } = useTransactionHistoryQuery({ startDay, endDay });

    const transactionList: TransactionDataType[] = useMemo(() => {
        const raw = transactionHistoryQuery.data ?? [];
        if (!transactionHistoryQuery.isFetched || !isTransactionHistoryQueryRetrieve) {
            return [];
        }

        const cutoff = transactionType.value === 'past-30-days' ? sub(new Date(), { days: 30 }) : sub(new Date(), { months: 12 });

        const filtered = raw.filter(tx => {
            const date = new Date(tx.transactionDate);
            return isAfter(date, cutoff) || isSameDay(date, cutoff);
        });

        return filtered.sort((a, b) => {
            return compareDesc(new Date(a.transactionDate), new Date(b.transactionDate));
        });
    }, [transactionType, transactionHistoryQuery, isTransactionHistoryQueryRetrieve]);

    const handleOptionTypeChange = (value: SelectCustomOption | null): void => {
        if (value) {
            setTransactionType(value);
        }
    };

    const exportCsv = useCallback((): void => {
        generateCsv<TransactionDataType>({
            data: transactionList,
            columns: ['Transaction Date', 'Description', 'Amount', 'Available Balance'],
            rowFormatter: item => {
                return [
                    formatDate(item.transactionDate, 'yyyy-MM-dd'),
                    item.description,
                    String(item.amount),
                    String(item.runningBalance)
                ];
            },
        });
    }, [transactionList]);

    if (transactionHistoryQuery.isFetching && !isTransactionHistoryQueryRetrieve) {
        return (
            <div>
                <LoadingIndicator className="sg-eg-loader" />
            </div>
        );
    }

    return (
        <div className="transaction-history">
            <h2 className="transaction-history-title">All Transactions</h2>

            <div className="display-transaction-options">
                <div className="display-transaction-select-dropdown">
                    <label htmlFor="transaction-history-display-options-select">Transactions to display</label>
                    <SelectCustom
                        id="transaction-history-display-options-select"
                        canUnsetValue={false}
                        options={transactionOptions}
                        onChange={handleOptionTypeChange}
                        value={transactionType}
                    />
                </div>
                <Button prefixIcon={'export'} onClick={exportCsv}>Export Visible Transactions to CSV</Button>
            </div>

            <TransactionHistoryTableResult transactions={transactionList} />
        </div>
    );
};

const queryClient = new QueryClient();

interface TransactionHistoryInitProps {
    el: HTMLDivElement;
}

const TransactionHistoryInit = ({ el }: TransactionHistoryInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <TransactionHistory />
        </QueryClientProvider>
    );
};

export default TransactionHistoryInit;
